<template>
  <v-card v-if="!preview" class="section-item mb-6">
    <!-- Section Header -->
    <v-card-title class="d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <!-- Reorder Arrows for Section -->
        <v-col cols="1" class="d-flex flex-column align-center mr-2 pa-0"
          style="max-height: 45px; height: 45px; max-width: 32px; overflow: hidden;">
          <v-btn icon small :color="index === 0 ?  'grey lighten-3' : 'black darken-2'"
            style="max-height: 17px;" @click="$emit('move-section-up', index)" :disabled="index === 0">
            <v-icon :color="index === 0 ? 'grey lighten-1' : 'black lighten-1'">
              mdi-chevron-up
            </v-icon>
          </v-btn>
          <v-btn icon small :color="isLastSection ? 'grey lighten-3' : 'black darken-2'"
            style="max-height: 17px;" @click="$emit('move-section-down', index)" :disabled="isLastSection">
            <v-icon :color="isLastSection ? 'grey lighten-1' : 'black lighten-1'">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </v-col>
      <v-text-field v-model="section.title" outlined dense clearable hide-details="auto"
      style="max-width: 200px;" class="mr-3" :label="$t('esgPlatform.esgReport.enterSectionTitle')"/>
      </div>
      <div class="d-flex align-center">
        <v-btn icon fab small class="primary" @click="showSettings = true">
          <v-icon color="white">mdi-cog</v-icon>
        </v-btn>
        <v-btn icon fab small class="ml-2 red" @click="$emit('delete-section', index)">
          <v-icon color="white">mdi-trash-can</v-icon>
        </v-btn>
      </div>
    </v-card-title>

    <!-- Section Blocks -->
    <v-card-text>
      <div v-for="(block, bIdx) in (section && Array.isArray(section.blocks) ? section.blocks : [])" :key="block._id || bIdx" class="d-flex align-start mb-3">
        <!-- Reorder Arrows for Block -->
        <v-col cols="1" class="d-flex flex-column align-center mr-2 pa-0"
          style="max-height: 45px; height: 45px; max-width: 32px; overflow: hidden;">
          <v-btn icon small :color="bIdx === 0 ? 'grey lighten-3' : 'black darken-2'"
            style="max-height: 15px;" @click="moveBlockUp(bIdx)" :disabled="bIdx === 0">
            <v-icon :color="bIdx === 0 ? 'grey lighten-1' : 'black lighten-1'">
              mdi-chevron-up
            </v-icon>
          </v-btn>
          <v-btn icon small :color="isLastBlock(bIdx) ? 'grey lighten-3' : 'black darken-2'"
            style="max-height: 15px;" @click="moveBlockDown(bIdx)" :disabled="isLastBlock(bIdx)">
            <v-icon :color="isLastBlock(bIdx) ? 'grey lighten-1' : 'black lighten-1'">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </v-col>

        <!-- Block Component -->
        <div class="flex-grow-1">
      <component
        :is="getComponent(block.type)"
        :block="block"
        :heading-size="section.headingSize"
        @update="updateBlock(bIdx, $event)"
        @delete="deleteBlock(bIdx)"
        :protocol-id="protocolId"
        :target-years="targetYears"
        :sub-organizations="subOrganizations"
      />
        </div>
      </div>
      <!-- Add Block Button -->
      <v-btn text class="mt-3 outline-0" @click="showBlockDialog = true">
        <v-icon fab color="white" class="primary text-black rounded-circle mr-3">mdi-plus</v-icon>{{ $t("esgPlatform.esgReport.addBlock") }}
      </v-btn>
    </v-card-text>

    <AddBlockDialog v-model="showBlockDialog" @save="addBlock" />
    <SectionSettingsDialog v-model="showSettings" :settings.sync="section.settings" />
  </v-card>
  <div v-else class="pa-6" :style="sectionStyle" style="margin-bottom: 25px;">
    <component
        v-for="(block, bIdx) in section.blocks"
        :key="block._id || bIdx"
        :is="getComponent(block.type)"
        :block="block"
        :preview="preview"
        :heading-size="section.headingSize"
        @update="updateBlock(bIdx, $event)"
        @delete="deleteBlock(bIdx)"
      />
  </div>
</template>

<script>
import AddBlockDialog from './SectionEditorDialog.vue';
import SectionSettingsDialog from './SectionSettingDialog.vue';
import HeadingBlock from './BlockTypes/HeadingBlock.vue';
import ParagraphBlock from './BlockTypes/ParagraphBlock.vue';
import ImageBlock from './BlockTypes/ImageBlock.vue';
import TableBlock from './BlockTypes/TableBlock.vue';
import StakeholdersBlock from './BlockTypes/StakeholdersBlock.vue';
import EsgProgressLineChartBlock from './BlockTypes/EsgProcessLineChartBlock.vue';
import EsgScorecardBlock from './BlockTypes/EsgScorecardBlock.vue';


export default {
  name: 'SectionItem',
  props: {
    section: Object,
    index: Number,
    totalSections: {
      type: Number,
      required: true
    },
    preview: {
      type: Boolean,
      default: false
    },
    protocolId: {
      type: String,
      default: null
    },
    targetYears: {
      type: Array,
      default: () => []
    },
    subOrganizations: {
      type: Array,
      default: () => []
    },
  },
  components: {
    AddBlockDialog,
    SectionSettingsDialog,
    HeadingBlock,
    ParagraphBlock,
    ImageBlock,
    TableBlock,
    StakeholdersBlock,
    EsgProgressLineChartBlock,
    EsgScorecardBlock
  },
  data() {
    return {
      showBlockDialog: false,
      showSettings: false
    };
  },
  computed: {
    sectionStyle() {
      const settings = this.section.settings || {};
      return {
        backgroundColor: settings.backgroundColor || '#fff',
        color: settings.fontColor || '#000',
        fontFamily: settings.font || 'inherit'
      };
    },
    isLastSection() {
      return this.index === this.totalSections - 1;
    }
  },
  methods: {
    getComponent(type) {
      return {
        heading: 'HeadingBlock',
        paragraph: 'ParagraphBlock',
        image: 'ImageBlock',
        table: 'TableBlock',
        stakeholders: 'StakeholdersBlock',
        esgProgressLineChart: 'EsgProgressLineChartBlock',
        esgScorecard: 'EsgScorecardBlock',
      }[type];
    },
    addBlock(newBlock) {
      console.log(newBlock);
      this.section.blocks.push(newBlock);
    },
    deleteBlock(i) {
      this.section.blocks.splice(i, 1);
    },
    updateBlock(i, newBlock) {
      this.$set(this.section.blocks, i, newBlock);
    },
    isLastBlock(index) {
      return index === this.section.blocks.length - 1;
    },
    moveBlockUp(index) {
      if (index <= 0) return;

      const temp = this.section.blocks[index];
      this.$set(this.section.blocks, index, this.section.blocks[index - 1]);
      this.$set(this.section.blocks, index - 1, temp);

      const swappedBlocks = [
        { _id: this.section.blocks[index - 1]?._id, order: index - 1 },
        { _id: this.section.blocks[index]?._id, order: index }
      ].filter(b => b._id);
      
      this.$emit('reorder-blocks', {
        sectionId: this.section._id,
        swappedBlocks: swappedBlocks
      });
    },
    moveBlockDown(index) {
      if (index >= this.section.blocks.length - 1) return;
            const temp = this.section.blocks[index];
      this.$set(this.section.blocks, index, this.section.blocks[index + 1]);
      this.$set(this.section.blocks, index + 1, temp);

      const swappedBlocks = [
        { _id: this.section.blocks[index]?._id, order: index },
        { _id: this.section.blocks[index + 1]?._id, order: index + 1 }
      ]. filter(b => b._id);
      
      this.$emit('reorder-blocks', {
        sectionId: this.section._id,
        swappedBlocks: swappedBlocks
      });
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 20px;
  font-weight: bold;
}

.section-item {
  margin-bottom: 25px;
}
</style>
